import type { CSSProperties } from "react";
import { fadeSrgb, mixSrgb } from "./colorMix";
import { COVER_ANGLES, SHAPE_COUNT, type Cover } from "./covers";

/**
 * Class surfaces. These functions return ONLY what is derived from data — the gradients.
 * Geometry, rings, shadows and neutral colours are design constants and live in Tailwind
 * tokens, applied by the components. The single exception is the swatch, whose width,
 * height and radius come from its `size` prop and therefore cannot be a static class.
 */

/** 4 discrete textures — each class gets one. */
export function coverPattern(shape: number): string {
  switch (shape % SHAPE_COUNT) {
    case 0:
      return "repeating-radial-gradient(circle at 112% 118%, rgba(255,255,255,.14) 0 1.5px, transparent 1.5px 21px)";
    case 1:
      return "repeating-linear-gradient(118deg, rgba(255,255,255,.10) 0 1.5px, transparent 1.5px 15px)";
    case 2:
      return "radial-gradient(58% 78% at 78% 12%, rgba(255,255,255,.22) 0%, transparent 60%), radial-gradient(46% 60% at 14% 96%, rgba(0,0,0,.18) 0%, transparent 66%)";
    default:
      return "conic-gradient(from 210deg at 88% 8%, rgba(255,255,255,.20) 0deg, transparent 92deg, transparent 268deg, rgba(255,255,255,.12) 360deg)";
  }
}

const angleOf = (cover: Cover): number => COVER_ANGLES[cover.shape % SHAPE_COUNT];

/** The colour, without the texture — see coverBackground and coverPattern. */
export function coverBackgroundBase(cover: Cover): string {
  return [
    `radial-gradient(78% 96% at 16% 4%, ${fadeSrgb(cover.c, 0.72)} 0%, transparent 58%)`,
    `radial-gradient(90% 84% at 96% 92%, ${cover.a} 0%, transparent 72%)`,
    `linear-gradient(${angleOf(cover)}deg, ${cover.a} 0%, ${cover.b} 52%, ${cover.c} 100%)`,
  ].join(",");
}

/**
 * Cover thumbnail background — used at every thumbnail size. Composited from the two functions
 * above: a caller that needs to animate the texture on its own (see ClassTile's hover) draws
 * coverBackgroundBase and coverPattern as two separate layers instead of this single property.
 */
function coverBackground(cover: Cover): string {
  return [coverPattern(cover.shape), coverBackgroundBase(cover)].join(",");
}

export function coverStyle(cover: Cover): CSSProperties {
  return { background: coverBackground(cover) };
}

/**
 * Class page banner. Darker than the thumbnail: an ink veil sits over the gradient so
 * white text stays legible. The veil is part of the single `background` value and cannot
 * be split into a token.
 */
function classHeroBackground(cover: Cover): string {
  return [
    "linear-gradient(90deg, rgba(12,16,24,.52) 0%, rgba(12,16,24,.34) 62%, rgba(12,16,24,.26) 100%)",
    coverPattern(cover.shape),
    `radial-gradient(64% 150% at 12% 0%, ${fadeSrgb(cover.c, 0.45)} 0%, transparent 58%)`,
    `radial-gradient(84% 190% at 98% 100%, ${cover.a} 0%, transparent 76%)`,
    `linear-gradient(${angleOf(cover)}deg, ${cover.a} 0%, ${cover.b} 100%)`,
  ].join(",");
}

export function classHeroStyle(cover: Cover): CSSProperties {
  return { background: classHeroBackground(cover) };
}

/** The rounded square of the class selector — the most reused shape in the product. */
export function classSwatchStyle(cover: Cover | null, size: number): CSSProperties {
  const borderRadius = Math.round(size * 0.32);

  if (!cover) {
    return { width: size, height: size, borderRadius };
  }

  const dot = Math.max(5, Math.round(size * 0.42));

  return {
    width: size,
    height: size,
    borderRadius,
    background: [
      `repeating-radial-gradient(circle at 118% 122%, rgba(255,255,255,.20) 0 1px, transparent 1px ${dot}px)`,
      `linear-gradient(152deg, ${mixSrgb(cover.c, 0.62, "#ffffff")} 0%, ${cover.b} 58%, ${cover.a} 100%)`,
    ].join(","),
  };
}

/**
 * Selected class pill: the class tint plus a 1.5px rule of the same colour.
 * Never ink — the class would lose its identity. Ink is only for entries that carry no
 * class identity ("Sans classe", "Toutes"), and those are handled with tokens.
 */
export function classPillStyle(cover: Cover): CSSProperties {
  return coverInk(cover, {
    background: fadeSrgb(cover.b, 0.14),
    boxShadow: `inset 0 0 0 1.5px ${cover.b}`,
  });
}

/**
 * An agenda slot is another class surface: its ground, its 4px left rule, its dot and its chip
 * are all derived from the cover. A slot with no class falls back to ink — which is exactly how
 * the reference tells a manual event from an imported course: no class, no colour.
 */
const SLOT_NEUTRAL = "rgb(var(--slot-neutral))";

const slotFade = (cover: Cover | null, tone: string, ratio: number): string =>
  cover ? fadeSrgb(tone, ratio) : `rgb(var(--slot-neutral) / ${ratio})`;

export type SlotState = "default" | "live";

const slotAccents = (cover: Cover | null) => ({
  mid: cover ? cover.b : SLOT_NEUTRAL,
  deep: cover ? cover.a : SLOT_NEUTRAL,
  light: cover ? cover.c : SLOT_NEUTRAL,
});

const SLOT_GROUND = "rgb(var(--surface-raised))";

/**
 * A class paints text in a deep tone on the light theme and in its light tone on the dark one. The
 * two tones travel as custom properties and the stylesheet picks one (`.cover-ink`), because a
 * component cannot know which theme it is drawn in without a second source of truth.
 */
const coverInk = (cover: Cover, style: CSSProperties): CSSProperties =>
  ({ ...style, "--cover-deep": cover.a, "--cover-light": cover.c }) as CSSProperties;

export function slotSurface(cover: Cover | null, state: SlotState = "default"): CSSProperties {
  const { mid, light } = slotAccents(cover);

  if (state === "live") {
    return {
      backgroundColor: SLOT_GROUND,
      backgroundImage: `linear-gradient(102deg, ${slotFade(cover, mid, 0.2)} 0%, ${slotFade(cover, light, 0.13)} 55%, transparent 100%)`,
      boxShadow: `var(--shadow-slot-live), inset 0 0 0 1.5px ${mid}`,
      borderLeftColor: mid,
    };
  }

  return {
    backgroundColor: SLOT_GROUND,
    backgroundImage: `linear-gradient(102deg, ${slotFade(cover, mid, 0.13)} 0%, ${slotFade(cover, light, 0.09)} 46%, transparent 100%)`,
    boxShadow: `inset 0 0 0 1px ${slotFade(cover, mid, 0.3)}`,
    borderLeftColor: mid,
  };
}

/** The dot on the chronological rail. */
export function slotDotStyle(cover: Cover | null): CSSProperties {
  const { mid, deep, light } = slotAccents(cover);
  return { background: `linear-gradient(160deg, ${light} 0%, ${mid} 52%, ${deep} 100%)` };
}

/** The class chip carried by a slot. */
export function slotChipStyle(cover: Cover | null): CSSProperties {
  const { mid, light } = slotAccents(cover);
  const style = {
    background: `linear-gradient(135deg, ${slotFade(cover, light, 0.44)} 0%, ${slotFade(cover, mid, 0.26)} 100%)`,
  };
  return cover ? coverInk(cover, style) : { ...style, color: "rgb(var(--ink))" };
}

/** Vertical rule of an agenda slot or a task row. */
export function classRailStyle(cover: Cover, height?: number): CSSProperties {
  return {
    background: `linear-gradient(180deg, ${cover.c} 0%, ${cover.b} 60%, ${cover.a} 100%)`,
    ...(height === undefined ? {} : { height }),
  };
}